import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WorkOrderItem {
  id: string;
  work_order_id: string;
  company_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  created_at: string;
  updated_at: string;
}

export interface CreateWorkOrderItemData {
  work_order_id: string;
  company_id: string;
  description: string;
  quantity: number;
  unit_price: number;
}

export interface UpdateWorkOrderItemData {
  id: string;
  description?: string;
  quantity?: number;
  unit_price?: number;
}

export function useWorkOrderItems(workOrderId: string | undefined) {
  return useQuery({
    queryKey: ['work-order-items', workOrderId],
    queryFn: async () => {
      if (!workOrderId) return [];
      
      const { data, error } = await supabase
        .from('fleet_work_order_items')
        .select('*')
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching work order items:', error);
        throw error;
      }
      
      return data as WorkOrderItem[];
    },
    enabled: !!workOrderId,
  });
}

export function useCreateWorkOrderItem(companyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Omit<CreateWorkOrderItemData, 'company_id'>) => {
      const { data: result, error } = await supabase
        .from('fleet_work_order_items')
        .insert({
          ...data,
          company_id: companyId,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as WorkOrderItem;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-items', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      toast.success('Item adicionado com sucesso');
    },
    onError: (error) => {
      console.error('Error creating work order item:', error);
      toast.error('Erro ao adicionar item');
    },
  });
}

export function useUpdateWorkOrderItem(companyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateWorkOrderItemData) => {
      const { data: result, error } = await supabase
        .from('fleet_work_order_items')
        .update(data)
        .eq('id', id)
        .eq('company_id', companyId)
        .select()
        .single();
      
      if (error) throw error;
      return result as WorkOrderItem;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-items', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      toast.success('Item atualizado com sucesso');
    },
    onError: (error) => {
      console.error('Error updating work order item:', error);
      toast.error('Erro ao atualizar item');
    },
  });
}

export function useDeleteWorkOrderItem(companyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, workOrderId }: { id: string; workOrderId: string }) => {
      const { error } = await supabase
        .from('fleet_work_order_items')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);
      
      if (error) throw error;
      return { id, workOrderId };
    },
    onSuccess: ({ workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-items', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      toast.success('Item removido com sucesso');
    },
    onError: (error) => {
      console.error('Error deleting work order item:', error);
      toast.error('Erro ao remover item');
    },
  });
}

// Bulk operations for the form (before work order is saved)
export interface LocalWorkOrderItem {
  tempId: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export function createLocalItem(description: string = '', quantity: number = 1, unitPrice: number = 0): LocalWorkOrderItem {
  return {
    tempId: crypto.randomUUID(),
    description,
    quantity,
    unit_price: unitPrice,
    total_price: quantity * unitPrice,
  };
}

export function calculateItemTotal(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

export function calculateTotalFromItems(items: LocalWorkOrderItem[]): number {
  return items.reduce((sum, item) => sum + item.total_price, 0);
}
